import { useFigureCaptionForElement } from '@alpod-ai-writer/plate-plugins/figure-caption/react';
import { useSourceCaptionForElement } from '@alpod-ai-writer/plate-plugins/source-caption/react';
import { type TTableElement } from '@alpod-ai-writer/plate-plugins/table';
import {
    TablePlugin,
    TableProvider,
    useTableBordersDropdownMenuContentState,
    useTableElement,
    useTableMergeState,
} from '@alpod-ai-writer/plate-plugins/table/react';
import type * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { cn, withRef } from '@udecode/cn';
import {
    PlateElement,
    useEditorPlugin,
    useEditorRef,
    useEditorSelector,
    useElement,
    usePluginOption,
    useReadOnly,
    useRemoveNodeButton,
    useSelected,
    withHOC,
} from '@udecode/plate/react';
import { BlockSelectionPlugin } from '@udecode/plate-selection/react';
import {
    AlignCenterIcon,
    AlignLeftIcon,
    AlignRightIcon,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    CombineIcon,
    Grid2X2Icon,
    LinkIcon,
    Link2OffIcon,
    PencilIcon,
    PencilOffIcon,
    SquareSplitHorizontalIcon,
    Trash2Icon,
    XIcon,
} from 'lucide-react';

import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuPortal, DropdownMenuTrigger } from '../common/dropdownMenu.component';
import { Popover, PopoverContent } from '../common/popover.component';
import { Toolbar, ToolbarButton, ToolbarGroup } from '../common/toolbar.component';
import {
    BorderAll,
    BorderBottom,
    BorderLeft,
    BorderNone,
    BorderRight,
    BorderTop,
} from './tableIcons';

export const TableElement = withHOC(
    TableProvider,
    withRef<typeof PlateElement>(({ children, className, ...props }, ref) => {
        const readOnly = useReadOnly();
        const isSelectionAreaVisible = usePluginOption(
            BlockSelectionPlugin,
            'isSelectionAreaVisible'
        );
        const hasControls = !readOnly && !isSelectionAreaVisible;
        const selected = useSelected();
        const element = useElement<TTableElement>();
        const {
            isSelectingCell,
            marginLeft,
            props: tableProps,
        } = useTableElement();

        const content = (
            <PlateElement
                className={cn(
                    className,
                    'overflow-x-auto py-5',
                    hasControls && '-ml-2 *:data-[slot=block-selection]:left-2'
                )}
                style={{ paddingLeft: marginLeft }}
                {...props}
            >
                <div className={cn(
                    'group/table relative w-fit',
                    element.align === 'center' && 'mx-auto',
                    element.align === 'right' && 'ml-auto',
                )}>
                    <table
                        ref={ref}
                        className={cn(
                            'mr-0 ml-px table h-px table-fixed border-collapse',
                            isSelectingCell && 'selection:bg-transparent'
                        )}
                        {...tableProps}
                    >
                        <tbody className="min-w-full">{children}</tbody>
                    </table>
                </div>
            </PlateElement>
        );

        if (readOnly || !selected) {
            return content;
        }

        return <TableFloatingToolbar>{content}</TableFloatingToolbar>;
    })
);

export const TableFloatingToolbar = withRef<typeof PopoverContent>(
    ({ children, ...props }, ref) => {
        const { tf } = useEditorPlugin(TablePlugin);
        const element = useElement<TTableElement>();
        const { props: buttonProps } = useRemoveNodeButton({ element });
        const collapsed = useEditorSelector(
            (editor) => !editor.api.isExpanded(),
            []
        );
        const { canMerge, canSplit } = useTableMergeState();
        const { hasCaption, insertCaption, removeCaption } = useFigureCaptionForElement(element);
        const { hasSource, insertSource, removeSource } = useSourceCaptionForElement(element);

        return (
            <Popover open={canMerge || canSplit || collapsed} modal={false}>
                <PopoverAnchor asChild>{children}</PopoverAnchor>
                <PopoverContent
                    ref={ref}
                    asChild
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    contentEditable={false}
                    {...props}
                >
                    <Toolbar
                        className="flex scrollbar-hide w-auto max-w-[80vw] flex-row overflow-x-auto rounded-md border bg-white p-1 shadow-md print:hidden"
                        contentEditable={false}
                    >
                        <ToolbarGroup>
                            {canMerge && (
                                <ToolbarButton
                                    onClick={() => tf.table.merge()}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Sloučit buňky"
                                >
                                    <CombineIcon />
                                </ToolbarButton>
                            )}
                            {canSplit && (
                                <ToolbarButton
                                    onClick={() => tf.table.split()}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Rozdělit buňky"
                                >
                                    <SquareSplitHorizontalIcon />
                                </ToolbarButton>
                            )}
                            <DropdownMenu modal={false}>
                                <DropdownMenuTrigger asChild>
                                    <ToolbarButton tooltip="Okraje buňky">
                                        <Grid2X2Icon />
                                    </ToolbarButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuPortal>
                                    <TableBordersDropdownMenuContent />
                                </DropdownMenuPortal>
                            </DropdownMenu>
                        </ToolbarGroup>
                        {collapsed && (
                            <ToolbarGroup>
                                <ToolbarButton
                                    onClick={() => tf.table.setAlign({ align: 'left' })}
                                    onMouseDown={(e) => e.preventDefault()}
                                    pressed={!element.align || element.align === 'left'}
                                    tooltip="Zarovnat tabulku vlevo"
                                >
                                    <AlignLeftIcon />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => tf.table.setAlign({ align: 'center' })}
                                    onMouseDown={(e) => e.preventDefault()}
                                    pressed={element.align === 'center'}
                                    tooltip="Zarovnat tabulku na střed"
                                >
                                    <AlignCenterIcon />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => tf.table.setAlign({ align: 'right' })}
                                    onMouseDown={(e) => e.preventDefault()}
                                    pressed={element.align === 'right'}
                                    tooltip="Zarovnat tabulku vpravo"
                                >
                                    <AlignRightIcon />
                                </ToolbarButton>
                            </ToolbarGroup>
                        )}
                        {collapsed && (
                            <ToolbarGroup>
                                <ToolbarButton tooltip="Smazat tabulku" {...buttonProps}>
                                    <Trash2Icon />
                                </ToolbarButton>
                            </ToolbarGroup>
                        )}
                        {collapsed && (
                            <ToolbarGroup>
                                <ToolbarButton
                                    onClick={() => {
                                        tf.insert.tableRow({ before: true });
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Vložit řádek před"
                                >
                                    <ArrowUp />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => {
                                        tf.insert.tableRow();
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Vložit řádek za"
                                >
                                    <ArrowDown />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => {
                                        tf.remove.tableRow();
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Smazat řádek"
                                >
                                    <XIcon />
                                </ToolbarButton>
                            </ToolbarGroup>
                        )}
                        {collapsed && (
                            <ToolbarGroup>
                                <ToolbarButton
                                    onClick={() => {
                                        tf.insert.tableColumn({ before: true });
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Vložit sloupec před"
                                >
                                    <ArrowLeft />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => {
                                        tf.insert.tableColumn();
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Vložit sloupec za"
                                >
                                    <ArrowRight />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => {
                                        tf.remove.tableColumn();
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    tooltip="Smazat sloupec"
                                >
                                    <XIcon />
                                </ToolbarButton>
                            </ToolbarGroup>
                        )}
                        {collapsed && (
                            <ToolbarGroup>
                                {!hasCaption && (
                                    <ToolbarButton
                                        onClick={() => {
                                            insertCaption('table');
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        tooltip="Vložit popisek"
                                    >
                                        <PencilIcon />
                                    </ToolbarButton>
                                )}
                                {hasCaption && (
                                    <ToolbarButton
                                        onClick={() => {
                                            removeCaption();
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        tooltip="Odebrat popisek"
                                    >
                                        <PencilOffIcon />
                                    </ToolbarButton>
                                )}
                                {!hasSource && (
                                    <ToolbarButton
                                        onClick={() => {
                                            insertSource();
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        tooltip="Vložit zdroj"
                                    >
                                        <LinkIcon />
                                    </ToolbarButton>
                                )}
                                {hasSource && (
                                    <ToolbarButton
                                        onClick={() => {
                                            removeSource();
                                        }}
                                        onMouseDown={(e) => e.preventDefault()}
                                        tooltip="Odebrat zdroj"
                                    >
                                        <Link2OffIcon />
                                    </ToolbarButton>
                                )}
                            </ToolbarGroup>
                        )}
                    </Toolbar>
                </PopoverContent>
            </Popover>
        );
    }
);

export const TableBordersDropdownMenuContent = withRef<
  typeof DropdownMenuPrimitive.Content
>((props, ref) => {
    const editor = useEditorRef();
    const {
        getOnSelectTableBorder,
        hasBottomBorder,
        hasLeftBorder,
        hasNoBorders,
        hasOuterBorders,
        hasRightBorder,
        hasTopBorder,
    } = useTableBordersDropdownMenuContentState();
    return (
        <DropdownMenuContent
            ref={ref}
            className={cn('min-w-[220px]')}
            onCloseAutoFocus={(e) => {
                e.preventDefault();
                editor.tf.focus();
            }}
            align="start"
            side="right"
            sideOffset={0}
            {...props}
        >
            <DropdownMenuGroup>
                <DropdownMenuCheckboxItem
                    checked={hasTopBorder}
                    onCheckedChange={getOnSelectTableBorder('top')}
                >
                    <BorderTop />
                    <div>Horní okraj</div>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                    checked={hasRightBorder}
                    onCheckedChange={getOnSelectTableBorder('right')}
                >
                    <BorderRight />
                    <div>Pravý okraj</div>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                    checked={hasBottomBorder}
                    onCheckedChange={getOnSelectTableBorder('bottom')}
                >
                    <BorderBottom />
                    <div>Spodní okraj</div>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                    checked={hasLeftBorder}
                    onCheckedChange={getOnSelectTableBorder('left')}
                >
                    <BorderLeft />
                    <div>Levý okraj</div>
                </DropdownMenuCheckboxItem>
            </DropdownMenuGroup>
            <DropdownMenuGroup>
                <DropdownMenuCheckboxItem
                    checked={hasNoBorders}
                    onCheckedChange={getOnSelectTableBorder('none')}
                >
                    <BorderNone />
                    <div>Bez okrajů</div>
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                    checked={hasOuterBorders}
                    onCheckedChange={getOnSelectTableBorder('outer')}
                >
                    <BorderAll />
                    <div>Vnější okraj</div>
                </DropdownMenuCheckboxItem>
            </DropdownMenuGroup>
        </DropdownMenuContent>
    );
});
